import {
  useGenerateReportFile,
  useMfCentralStatus,
  webDownload,
} from "creso-core/hooks";
import { EPOCH_DATE } from "creso-core/constants";
import { format } from "date-fns";
import { RefreshCwIcon } from "lucide-react";
import {
  cloneElement,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { toast } from "sonner";
import {
  Button,
  DatePicker as DSDatePicker,
  DialogDescription,
  DialogHeader,
  DialogContent,
  DialogFooter,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  V2Dialog,
  V2Label,
  cn,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "web-design-system";
import { getFinancialYear } from "creso-core/utils";
import { useDownloadPdfReport } from "../../hooks";
import { getPdfDownloadUrl, handleError } from "../../utils";
import type { ReportClient, ReportFamily } from "./types";

export type PortfolioValuationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Host-app hook for the "refresh holdings" affordance. The refresh button is
   * only rendered when this is supplied (client mode only).
   */
  onImportHoldings?: () => void;
  /**
   * Host-app account picker, rendered full width as the first form row.
   */
  accountPicker?: ReactNode;
} & (
  | { client: ReportClient; family?: never }
  | { family: ReportFamily; client?: never }
);

function getMemberInvestmentAccount(member: {
  investment_account_id?: string | null;
}) {
  return member.investment_account_id || "";
}

const SOURCE = [
  {
    label: "Internal",
    value: "INTERNAL",
  },
  {
    label: "External",
    value: "EXTERNAL",
  },
  {
    label: "All",
    value: "OVERALL",
  },
] as const;

const DATE_RANGE_OPTIONS = [
  {
    label: "Till today",
    value: "till_today",
  },
  {
    label: "Custom Date",
    value: "custom",
  },
] as const;

type DateRangeType = "till_today" | "custom" | `fy_${number}`;

/** Keeps each tab the same flex width whether or not a tooltip wraps the trigger. */
function TabsToolbarCell({
  showTooltip,
  tooltipMessage = "",
  equalWidth = true,
  children,
}: {
  showTooltip: boolean;
  tooltipMessage?: string;
  equalWidth?: boolean;
  children: ReactElement<{ className?: string; disabled?: boolean }>;
}) {
  const trigger = cloneElement(children, {
    className: cn(
      children.props.className,
      equalWidth ? "w-full min-w-0" : "w-auto",
      showTooltip && "pointer-events-none cursor-not-allowed"
    ),
  });
  return (
    <span
      className={cn(
        "flex",
        equalWidth ? "min-w-0 flex-1 basis-0" : "w-auto flex-none"
      )}
    >
      {showTooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn(
                "flex cursor-not-allowed",
                equalWidth ? "w-full min-w-0" : "w-auto"
              )}
            >
              {trigger}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" portal>
            {tooltipMessage}
          </TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}
    </span>
  );
}

export function PortfolioValuationDialog(props: PortfolioValuationDialogProps) {
  const { client, family, open, onOpenChange, onImportHoldings } = props;
  const { accountPicker } = props;
  const isFamilyMode = !!family;

  const investmentAccount = isFamilyMode
    ? getMemberInvestmentAccount(family.members?.[0] ?? {})
    : (client?.investment_account ?? "");

  const [downloadPdfReport, isGenerating] = useDownloadPdfReport();

  const { data: mfCentralStatus } = useMfCentralStatus(
    investmentAccount,
    !!investmentAccount
  );

  const currentFinancialYear = useMemo(() => getFinancialYear(new Date()), []);
  const FY_YEARS = useMemo(
    () => Array.from({ length: 3 }, (_, i) => currentFinancialYear - i),
    [currentFinancialYear]
  );

  const [selectedDateRange, setSelectedDateRange] =
    useState<DateRangeType>("till_today");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [selectedSource, setSelectedSource] =
    useState<(typeof SOURCE)[number]["value"]>("INTERNAL");
  const [fileType, setFileType] = useState<"pdf" | "excel">("pdf");

  const { mutateAsync: generateReportFile } = useGenerateReportFile<
    "holdings" | "statement"
  >();

  const [includeTransactions, setIncludeTransactions] = useState(false);

  const isCustomDateRange = selectedDateRange === "custom";
  const isTillToday = selectedDateRange === "till_today";

  const getDateRange = () => {
    if (selectedDateRange === "till_today") {
      return {};
    }

    if (selectedDateRange === "custom") {
      if (!fromDate || !toDate) {
        toast.error("Please select both from date and to date");
        return null;
      }

      if (new Date(fromDate) > new Date(toDate)) {
        toast.error("From date should be before to date");
        return null;
      }

      return { start_date: fromDate, end_date: toDate };
    }

    const year = Number(selectedDateRange.replace("fy_", ""));
    return {
      start_date: format(new Date(year, 3, 1), "yyyy-MM-dd"),
      end_date: format(new Date(year + 1, 2, 31), "yyyy-MM-dd"),
    };
  };

  const handleDateRangeChange = (value: string) => {
    const nextValue = value as DateRangeType;
    setSelectedDateRange(nextValue);

    if (nextValue === "custom") {
      setSelectedSource("INTERNAL");
      setFileType("excel");
    }
  };

  const handleFamilyDownload = async () => {
    if (!family) return;
    const dateRange = getDateRange();
    if (dateRange === null) return;

    try {
      const fileName = `${family.name}-${format(new Date(), "dd-MM-yyyy")}-portfolio`;
      const today = format(new Date(), "yyyy-MM-dd");
      const startDate =
        selectedDateRange === "till_today"
          ? EPOCH_DATE
          : (dateRange.start_date ?? "");
      const endDate =
        selectedDateRange === "till_today" ? today : (dateRange.end_date ?? "");

      if (fileType === "pdf") {
        const urlSearchParams = new URLSearchParams({
          source: selectedSource,
          start_date: startDate,
          end_date: endDate,
          date_range: selectedDateRange,
          include_transactions: String(includeTransactions),
        });

        const downloadUrl = getPdfDownloadUrl(
          `/reports/families/${family.id}/portfolio?${urlSearchParams.toString()}`
        );
        await downloadPdfReport(downloadUrl, `${fileName}.pdf`, {
          loading: "Generating portfolio valuation report",
        });
      } else {
        const result = await generateReportFile({
          file_type: fileType,
          family_id: family.id,
          type: "holdings",
          source: selectedSource,
          start_date: startDate,
          end_date: endDate,
          additional_details: {
            include_transactions: includeTransactions,
          },
        });
        if (!("report_url" in result) || !result.report_url) {
          throw new Error("Failed to generate portfolio valuation report");
        }
        const reportUrl = result.report_url;
        const fileExtension = reportUrl.split(".").pop();
        const response = await fetch(
          `${reportUrl}?source=${selectedSource}&q=${new Date().toISOString()}`
        );
        if (!response.ok) {
          throw new Error("Failed to generate portfolio valuation report");
        }
        const blob = await response.blob();
        await webDownload(blob, `${fileName}.${fileExtension}`);
        toast.success("Portfolio valuation report generated successfully");
      }
    } catch (error) {
      handleError(error, "Failed to generate portfolio valuation report");
    }
  };

  const handleClientDownload = async () => {
    if (!client) return;
    const dateRange = getDateRange();
    if (dateRange === null) return;

    const reportType =
      selectedDateRange === "till_today" ? "holdings" : "statement";

    try {
      const fileName = `${client.name}-${format(new Date(), "dd-MM-yyyy")}-portfolio`;
      if (fileType === "pdf") {
        const today = format(new Date(), "yyyy-MM-dd");
        const startDate =
          selectedDateRange === "till_today"
            ? EPOCH_DATE
            : (dateRange.start_date ?? "");
        const endDate =
          selectedDateRange === "till_today"
            ? today
            : (dateRange.end_date ?? "");

        const urlSearchParams = new URLSearchParams({
          source: selectedSource,
          start_date: startDate,
          end_date: endDate,
          date_range: selectedDateRange,
          include_transactions: String(includeTransactions),
        });

        const downloadUrl = getPdfDownloadUrl(
          `/reports/clients/${client.id}/portfolio?${urlSearchParams.toString()}`
        );
        await downloadPdfReport(downloadUrl, `${fileName}.pdf`, {
          loading: "Generating portfolio valuation report",
        });
      } else {
        const result = await generateReportFile({
          file_type: fileType,
          investment_account_guid: client.investment_account ?? "",
          type: reportType,
          source: selectedSource,
          ...(dateRange.start_date ? { start_date: dateRange.start_date } : {}),
          ...(dateRange.end_date ? { end_date: dateRange.end_date } : {}),
          additional_details: {
            include_transactions: includeTransactions,
          },
        });
        if (!("report_url" in result) || !result.report_url) {
          throw new Error("Failed to generate portfolio valuation report");
        }
        const reportUrl = result.report_url;
        const fileExtension = reportUrl.split(".").pop();
        const response = await fetch(
          `${reportUrl}?source=${selectedSource}&q=${new Date().toISOString()}`
        );
        if (!response.ok) {
          throw new Error("Failed to generate capital gains report");
        }
        const blob = await response.blob();
        await webDownload(blob, `${fileName}.${fileExtension}`);
        toast.success("Portfolio valuation report generated successfully");
      }
    } catch (error) {
      handleError(error, "Failed to generate portfolio valuation report");
    }
  };

  return (
    <V2Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-6 w-100 gap-8">
        <DialogHeader>
          <DialogTitle className="!text-lg">
            Portfolio Valuation Report
          </DialogTitle>
          <DialogDescription className="text-muted-foreground gap-2 flex flex-row items-center text-sm">
            {mfCentralStatus?.last_fetch_at
              ? `Holdings last updated ${Math.floor(
                  (new Date().getTime() -
                    new Date(mfCentralStatus.last_fetch_at).getTime()) /
                    (1000 * 60 * 60 * 24)
                )} days ago`
              : ""}
            {!isFamilyMode &&
            onImportHoldings &&
            mfCentralStatus?.last_fetch_at ? (
              <Button onClick={onImportHoldings} size="icon" variant="ghost">
                <RefreshCwIcon className={cn("size-4")} />
              </Button>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-8 -mt-4">
          {accountPicker}
          <div className="flex flex-col gap-3">
            <div className="flex flex-row items-center justify-between gap-3">
              <V2Label
                className={cn("shrink-0 whitespace-nowrap text-sm font-normal")}
              >
                Date range
              </V2Label>
              <div className="min-w-0 max-w-56 flex-1">
                <Select
                  value={selectedDateRange}
                  onValueChange={handleDateRangeChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select date range" />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_RANGE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                    {FY_YEARS.map((year) => (
                      <SelectItem key={year} value={`fy_${year}`}>
                        Apr {year} - Mar {year + 1}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {isCustomDateRange ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="min-w-0">
                  <DSDatePicker
                    value={fromDate}
                    onChange={setFromDate}
                    placeholder="From date"
                    yearRange={{
                      from: currentFinancialYear - 2,
                      to: currentFinancialYear,
                    }}
                  />
                </div>
                <div className="min-w-0">
                  <DSDatePicker
                    value={toDate}
                    onChange={setToDate}
                    placeholder="To date"
                    yearRange={{
                      from: currentFinancialYear - 2,
                      to: currentFinancialYear,
                    }}
                    disabled={(date: Date) =>
                      fromDate ? date < new Date(fromDate) : false
                    }
                  />
                </div>
              </div>
            ) : null}
          </div>
          <div className="flex flex-row items-center justify-between gap-3">
            <V2Label
              className={cn("shrink-0 whitespace-nowrap text-sm font-normal")}
            >
              Source
            </V2Label>
            <div className="min-w-0 max-w-56 flex-1">
              <Tabs
                value={selectedSource}
                onValueChange={(value) =>
                  setSelectedSource(value as (typeof SOURCE)[number]["value"])
                }
              >
                <TabsList className="w-full justify-start">
                  {SOURCE.map((s) => {
                    const sourceDisabled =
                      !isTillToday &&
                      (s.value === "EXTERNAL" || s.value === "OVERALL");
                    return (
                      <TabsToolbarCell
                        key={s.value}
                        showTooltip={sourceDisabled}
                        equalWidth={false}
                        tooltipMessage="Only internal holdings can be used with a custom date range or financial year."
                      >
                        <TabsTrigger value={s.value} disabled={sourceDisabled}>
                          {s.label}
                        </TabsTrigger>
                      </TabsToolbarCell>
                    );
                  })}
                </TabsList>
              </Tabs>
            </div>
          </div>

          <div className="flex flex-row items-center gap-2 w-full justify-between">
            <V2Label
              className={cn("shrink-0 whitespace-nowrap text-sm font-normal")}
              htmlFor="include-transactions"
            >
              Include transactions
            </V2Label>
            <Switch
              size="lg"
              onCheckedChange={(checked) => setIncludeTransactions(!!checked)}
              checked={includeTransactions}
              id="include-transactions"
            />
          </div>
          <div className="flex flex-row items-center justify-between gap-3">
            <V2Label
              className={cn("shrink-0 whitespace-nowrap text-sm font-normal")}
            >
              File type
            </V2Label>
            <div className="min-w-0 max-w-56 flex-1">
              <Tabs
                value={fileType}
                onValueChange={(v) => setFileType(v as "pdf" | "excel")}
              >
                <TabsList className="w-full">
                  <TabsToolbarCell showTooltip={false}>
                    <TabsTrigger value="pdf">PDF</TabsTrigger>
                  </TabsToolbarCell>
                  <TabsToolbarCell showTooltip={false}>
                    <TabsTrigger value="excel">Excel</TabsTrigger>
                  </TabsToolbarCell>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            className="w-full"
            onClick={isFamilyMode ? handleFamilyDownload : handleClientDownload}
            disabled={isGenerating}
          >
            {isGenerating ? "Generating..." : "Download"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </V2Dialog>
  );
}
